import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabaseClient';

const updatePasswordSchema = z.object({
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type UpdatePasswordFormData = z.infer<typeof updatePasswordSchema>;

export default function UpdatePasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [isExchanging, setIsExchanging] = useState<boolean>(false);
  const [isSessionReady, setIsSessionReady] = useState<boolean>(false);
  const [resendEmail, setResendEmail] = useState<string>('');
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<UpdatePasswordFormData>({
    resolver: zodResolver(updatePasswordSchema),
  });

  // Detect if the URL contains a recovery token without automatically consuming it
  const hasRecoveryParams = useMemo(() => {
    const hash = location.hash || '';
    // Supabase recovery links include these in the URL hash
    return hash.includes('type=recovery') && hash.includes('access_token=');
  }, [location.hash]);

  useEffect(() => {
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event) => {
      if (event === 'PASSWORD_RECOVERY') {
        // User has clicked the recovery link
        console.log('Password recovery mode');
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  // If the user already has an active session (e.g., signed in), allow immediate password update
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setIsSessionReady(true);
    });
  }, []);

  // In the nonce-backed flow, arriving here means the user already confirmed intent.
  // Auto-exchange the recovery tokens into a session for a smoother UX.
  useEffect(() => {
    if (hasRecoveryParams && !isSessionReady && !isExchanging) {
      void exchangeRecoveryFromUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRecoveryParams, isSessionReady, isExchanging]);

  const exchangeRecoveryFromUrl = async () => {
    setError(null);
    setInfo(null);
    setIsExchanging(true);
    try {
      const hashParams = new URLSearchParams((location.hash || '').replace(/^#/, ''));
      const access_token = hashParams.get('access_token') || '';
      const refresh_token = hashParams.get('refresh_token') || '';
      if (!access_token || !refresh_token) {
        throw new Error('Reset link is missing required tokens. Please request a new one.');
      }
      const { data, error } = await supabase.auth.setSession({ access_token, refresh_token });
      if (error) throw error;
      // Clean the URL and replace history entry to prevent going back to token URL
      window.history.replaceState({},document.title,window.location.pathname + window.location.search);
      // Additionally, push a new clean state to prevent back navigation to tokens
      window.history.pushState({}, document.title, window.location.pathname + window.location.search);
      setIsSessionReady(!!data.session);
      if (!data.session) setError('We could not start a secure session. Please request a new reset link.');
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Invalid or expired link. Please request a new one.';
      setError(message);
    } finally {
      setIsExchanging(false);
    }
  };

  const onSubmit = async (data: UpdatePasswordFormData) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: data.password,
      });

      if (error) throw error;
      navigate('/login?reset=success');
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An error occurred');
    }
  };

  const resendLink = async () => {
    setError(null);
    setInfo(null);
    if (!resendEmail || !resendEmail.includes('@')) {
      setError('Enter the email associated with your account to resend the link.');
      return;
    }
    try {
      const { data: functionResponse, error: functionError } = await supabase.functions.invoke('generate-password-reset-webhook', {
        body: { email: resendEmail },
      });
      if (functionError) throw new Error(functionError.message || 'Failed to resend reset link.');
      if (functionResponse?.error) throw new Error(functionResponse.error);
      setInfo(functionResponse?.message || 'If an account exists, we sent a fresh reset link.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to resend reset link.');
    }
  };

  return (
    <div className="min-h-screen bg-soft-light-gray flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-slate-gray">Update your password</h2>
          {!isSessionReady && hasRecoveryParams && (
            <p className="mt-2 text-sm text-slate-gray">Confirm to securely continue your password reset.</p>
          )}
          {!isSessionReady && !hasRecoveryParams && (
            <p className="mt-2 text-sm text-slate-gray">If you reached this page from a reset email and it isn’t working, request a fresh link below.</p>
          )}
        </div>

        {/* Step 0: Guarded token exchange */}
        {!isSessionReady && hasRecoveryParams && (
          <div className="mt-8 space-y-6 bg-white p-8 rounded-lg shadow">
            {error && (
              <div className="bg-red-50 text-red-500 p-3 rounded-md text-sm">{error}</div>
            )}
            {info && (
              <div className="bg-green-50 text-green-700 p-3 rounded-md text-sm">{info}</div>
            )}
            <Button className="w-full" onClick={exchangeRecoveryFromUrl} disabled={isExchanging}>
              {isExchanging ? 'Preparing secure reset…' : 'Continue to secure reset'}
            </Button>
          </div>
        )}

        {/* Step 1: Password form once session is present */}
        {isSessionReady && (
          <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-6 bg-white p-8 rounded-lg shadow">
            {error && (
              <div className="bg-red-50 text-red-500 p-3 rounded-md text-sm">{error}</div>
            )}

            <div className="space-y-4">
              <div>
                <Label htmlFor="password">New Password</Label>
                <Input
                  id="password"
                  type="password"
                  {...register('password')}
                  className={errors.password ? 'border-red-500' : ''}
                />
                {errors.password && (
                  <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  {...register('confirmPassword')}
                  className={errors.confirmPassword ? 'border-red-500' : ''}
                />
                {errors.confirmPassword && (
                  <p className="text-red-500 text-sm mt-1">{errors.confirmPassword.message}</p>
                )}
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Updating password...' : 'Update password'}
            </Button>
          </form>
        )}

        {/* Step 2: Recovery if token was consumed or missing */}
        {!isSessionReady && !hasRecoveryParams && (
          <div className="mt-8 space-y-6 bg-white p-8 rounded-lg shadow">
            {error && (
              <div className="bg-red-50 text-red-500 p-3 rounded-md text-sm">{error}</div>
            )}
            {info && (
              <div className="bg-green-50 text-green-700 p-3 rounded-md text-sm">{info}</div>
            )}
            <div className="space-y-4">
              <div>
                <Label htmlFor="resendEmail">Email address</Label>
                <Input
                  id="resendEmail"
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>
              <Button className="w-full" type="button" onClick={resendLink}>Send me a new reset link</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}